﻿import { useState, useEffect, RefObject, useRef, useImperativeHandle } from 'react'
import ReactDOM from 'react-dom'
import { createContainer } from 'unstated-next'
import { api } from '../../../infrastructure/api'
import guid, { Guid } from '../../../infrastructure/guid'
import {
    Deal, Counterparty, DutyStatus, Company, Site, SupplyProduct, DealAssignee, DealKind, DuplicateDealCommand,
    CollapsableSection, GetIsIn, DealMovementItem, CreateMovementFromDeal, Period, DealFilter, DealForecastMktSales,
    SapCounterparty, DealMovementType, DealPriceIndex, DealPricingPeriod, SapShipTo, MultiplePricingBlockType, DealFieldUnit,
    DealPricingType, DealFieldGroup, DealTypes, DealStatus, GetMovementType, ForecastDailyPurchaseStatus, DealStatusDialog, VesselInfo
} from '../dealModels'
import { snackbars } from '../../../infrastructure/snackbars'
import { t } from '../../../infrastructure/i18nextHelper'
import { useFieldStatus } from '../../common/fieldsStatus'
import { QuantityUnitPrice } from '../../administration/masterData/quantityUnitPrice'
import { UserContextContainer, hasClaim, hasDealClaim, hasScope2OnClaim } from '../../../infrastructure/signIn/userContext'
import { hasFeature } from '../../../infrastructure/feature'
import { MovementListItem, MeanOfTransportation, MovementType, MovementStatus } from '../../stock/stockModels'
import moment from 'moment'
import { usePrevious } from '../../../infrastructure/customHooks'
import { setInLocalStorage } from '../../../infrastructure/localStorage'
import { DealDuplicatePeriod } from '../../common/duplicate/duplicateDealDialog'
import { PriceIndex } from '../../pricings/pricingModels'
import { dealApi } from '../dealApis'
import { isoDateFormat } from '../../../infrastructure/date'
import { Claims } from '../../../infrastructure/signIn/models'
import { PricingPeriodRule } from '../../administration/masterData/pricingPeriodRule'

function useDealDialog() {
    let [isOpen, setIsOpen] = useState<boolean>(false)
    let previousIsOpen = usePrevious(isOpen)
    return { isOpen, setIsOpen, previousIsOpen }
}

const defaultOpenSections: { [T in CollapsableSection]: boolean } =
    { 'pricingInformation': false, 'sap': false, 'movements': false, 'DealProvisionalPrices': false, 'DealFinalPrices': false }

let getOpenSectionsFromLocalStorage = (): { [T in CollapsableSection]: boolean } => {
    let dealOpenSectionsValue = localStorage.getItem('dealOpenSections')
    if (!!dealOpenSectionsValue) {
        let dealOpenSections = JSON.parse(dealOpenSectionsValue)
        return dealOpenSections
    }
    return defaultOpenSections
}

function useDeal() {
    let dialog = DealDialogContainer.useContainer()
    let [deal, setDeal] = useState<Deal | null>(null)
    let [dealId, setDealId] = useState<Guid | null>(null)
    let [counterpartys, setCounterpartys] = useState<Counterparty[]>([])
    let [allCounterpartys, setAllCounterpartys] = useState<Counterparty[]>([])
    let [priceIndexes, setPriceIndexes] = useState<PriceIndex[]>([])
    let [pricingPeriodRules, setPricingPeriodRules] = useState<PricingPeriodRule[]>([])
    let [dutyStatuses, setDutyStatuses] = useState<DutyStatus[]>([])
    let [companys, setCompanys] = useState<Company[]>([])
    let [paymentTerms, setPaymentTerms] = useState<string[]>([])
    let [sites, setSites] = useState<Site[]>([])
    let [products, setProducts] = useState<SupplyProduct[]>([])
    let [associatedVessels, setAssociatedVessels] = useState<VesselInfo[]>([])
    let [assigneeChoices, setAssigneeChoices] = useState<DealAssignee[]>([])
    let [unit, setUnit] = useState<string | undefined>()
    let [currency, setCurrency] = useState<string | undefined>()
    let [dealTypes, setDealTypes] = useState<string[]>([])
    let [quantityUnitPrices, setQuantityUnitPrices] = useState<QuantityUnitPrice[]>([])
    let [openSections, setOpenSections] =
        useState<{ [T in CollapsableSection]: boolean }>(getOpenSectionsFromLocalStorage())

    let [assignableMovements, setAssignableMovements] = useState<MovementListItem[] | null>(null)
    let [assignConfirmDialogOpen, setAssignConfirmDialogOpen] = useState<boolean>(false)
    let [selectedMovements, setSelectedMovements] = useState<MovementListItem[] | null>(null)
    let [unAssignConfirmDialogOpen, setUnAssignConfirmDialogOpen] = useState<boolean>(false)
    let [dealMovement, setDealMovement] = useState<DealMovementItem | null>(null)
    let [meanOfTransportations, setMeanOfTransportations] = useState<MeanOfTransportation[]>([])
    let [sapCounterpartys, setSapCounterpartys] = useState<SapCounterparty[]>([])
    let [shipTo, setShipTo] = useState<SapShipTo[]>([])

    let [filters, setFilters] = useState<DealFilter>(getLocalStorageFilters())

    let [lastCorrelationId, setLastCorrelationId] = useState<string | undefined>()
    let [forecastDailyPurchaseStatus, setForecastDailyPurchaseStatus] = useState<string>(ForecastDailyPurchaseStatus.None)
    let [dealStatusDialog, setDealStatusDialog] = useState<DealStatusDialog>({ isOpen: false, title: '', description: '' })
    let [duplicatePeriod, setDuplicatePeriod] = useState<DealDuplicatePeriod>({ start: '', end: '' })
    let [isPeriodMode, setIsPeriodMode] = useState(true)
    let [defaultQuantityPriceUnit, setDefaultQuantityPriceUnit] = useState<string | null>(null)
    let fieldStatus = useFieldStatus<Deal>()
    let costStatus = useFieldStatus<{ [P in string]: string }>()
    let user = UserContextContainer.useContainer()
    let previousQuantityPriceUnit = usePrevious(deal?.quantityPriceUnit)

    useEffect(() => {
        if (!deal) return
        fieldStatus.initPropertyTracking(deal)
        costStatus.initPropertyTracking((deal.fields ?? []).map(x => x.code))

        if (hasFeature('LimitExchangeRateToIndexDates') && deal.exchangeRateLimitToIndexDates === null) {
            setDeal({ ...deal, exchangeRateLimitToIndexDates: true })
        }
    }, [deal?.id])

    useEffect(() => {
        (async () => {
            let unit = await getUnitFromDealProduct(deal?.productId)
            setUnit(unit)
        })()
    }, [deal?.productId])

    useEffect(() => {
        (async () => {
            if (!!deal?.counterpartyId && !!deal?.company) {
                setShipTo(await dealApi.getSapCounterpartyShipTo({ company: deal?.company, counterpartyId: deal?.counterpartyId }))
            }
        })()
    }, [deal?.counterpartyId, deal?.company])

    useEffect(() => {
        if (!deal || (locked() && hasFeature('AdditionalPremiumUnit')) || !previousQuantityPriceUnit) return
        let newFinalPricingPeriods = resetPricingPeriodsAfterPricingChange(deal.finalPricing.pricingPeriods, deal.finalPricing.priceIndexes, 'Final')
        let newProvisionalPricingPeriods = resetPricingPeriodsAfterPricingChange(deal.provisionalPricing.pricingPeriods, deal.provisionalPricing.priceIndexes, 'Provisional')
        setDeal({
            ...deal,
            finalPricing: { ...deal.finalPricing, pricingPeriods: newFinalPricingPeriods },
            provisionalPricing: { ...deal.provisionalPricing, pricingPeriods: newProvisionalPricingPeriods }
        })
    }, [deal?.quantityPriceUnit])

    useEffect(() => {
        (async () => {
            if (!!deal?.company && !!deal?.productId) {
                let priceIndexes = await dealApi.getPriceIndexes(deal?.company, deal?.productId)
                setPriceIndexes(priceIndexes)
            }
        })()
    }, [deal?.company, deal?.productId])

    useEffect(() => {
        (async () => {
            let currency = await getCurrencyFromDealCompany(deal?.company)
            setCurrency(currency)
            setAssigneeChoices([])
            if (deal && deal!.company) {
                let assignees = await dealApi.getAssignees(deal!.company)
                setAssigneeChoices(assignees)
                if (user.profile?.userName && (!deal.assignee || !deal.frontOfficer)) {
                    let defaultAssignee = assignees.find(x => x.userName === user.profile?.userName)
                    setDeal({
                        ...deal,
                        assignee: deal.assignee ?? defaultAssignee ?? assignees[0],
                        frontOfficer: deal.frontOfficer ?? defaultAssignee?.userName ?? null
                    })
                }
            }
        })()
    }, [deal?.company, companys])

    let getAssignableMovements = async () => {
        if (!(hasDealClaim() && canAssignMovement())) return
        let queryString = createMovementQueryObject()
        let movements = await api.get<MovementListItem[]>('stock/movement', queryString)
        let assignableMovements = movements.filter(x => !x.associatedDealId && (!x.counterparty || x.counterparty === deal?.counterpartyId))
        setAssignableMovements(assignableMovements)
    }

    let assignableMovementsDependencies = [
        deal?.site,
        deal?.company,
        deal?.dutyStatus,
        deal?.productId,
        deal?.movementType,
        deal?.counterpartyId,
        deal?.validFrom,
        deal?.validTo
    ]

    useEffect(() => {
        getAssignableMovements()
    }, assignableMovementsDependencies)

    useEffect(() => {
        if (deal)
            getForecastDailyPurchaseStatus(deal!)
    }, [deal?.validFrom, deal?.validTo, deal?.movementType, deal?.meanOfTransportation])

    useEffect(() => {
        initializeQuantityPriceUnit()
    }, [deal?.company, deal?.productId, deal?.movementType, deal?.dealType, quantityUnitPrices, companys, currency, unit])

    async function initializeQuantityPriceUnit() {
        if (deal?.company && deal?.productId && deal?.movementType && deal?.dealType === "BORROWLOAN") {
            if (!quantityUnitPrices.length) return

            let companyCurrency = companys.find(x => x.code === deal.company)?.companyCurrency ?? currency
            let resolveUnitPrice = (unitPrice: string) => unitPrice.replace("CompanyCurrency", companyCurrency ?? "CompanyCurrency")

            let { quantityPriceUnit } = await dealApi.getDefaultQuantityPriceUnit(deal!.company, deal!.productId as string, deal!.movementType, deal!.dealType)
            if (quantityPriceUnit === null) {
                setDefaultQuantityPriceUnit(null)
                return
            }

            let matchedQuantityUnitPrice = quantityUnitPrices.find(x => resolveUnitPrice(x.unitPrice) === quantityPriceUnit)
            let resolvedCurrency: string | null = matchedQuantityUnitPrice?.currency
                ? resolveUnitPrice(matchedQuantityUnitPrice.currency)
                : (companyCurrency ? companyCurrency : null)

            setDefaultQuantityPriceUnit(quantityPriceUnit)
            setDeal({
                ...deal,
                quantityUnit: unit ?? null,
                quantityUnitOverwritten: false,
                quantityPriceUnit: quantityPriceUnit,
                quantityPriceUnitOverwritten: false,
                currency: resolvedCurrency
            })
        }
        else
            setDefaultQuantityPriceUnit(null)
    }

    let addOrUpdateDealPriceIndex = (priceIndex: DealPriceIndex, type: MultiplePricingBlockType) => {
        if (!deal) return
        let existingPriceIndexes = type === 'Final' ? deal.finalPricing.priceIndexes : deal.provisionalPricing.priceIndexes
        let existingPricingPeriods = type === 'Final' ? deal.finalPricing.pricingPeriods : deal.provisionalPricing.pricingPeriods
        let newPriceIndexes: DealPriceIndex[] = existingPriceIndexes.some(x => priceIndex.id == x.id)
            ? existingPriceIndexes.map(x => (x.id === priceIndex.id) ? priceIndex : x)
            : existingPriceIndexes.concat(priceIndex)

        let newPricingPeriods = resetPricingPeriodsAfterPricingChange(existingPricingPeriods, newPriceIndexes, type)
        if (type === 'Final')
            setDeal({ ...deal, finalPricing: { ...deal.finalPricing, priceIndexes: newPriceIndexes, pricingPeriods: newPricingPeriods } })
        if (type === 'Provisional')
            setDeal({ ...deal, provisionalPricing: { ...deal.provisionalPricing, priceIndexes: newPriceIndexes, pricingPeriods: newPricingPeriods } })
    }

    let addOrUpdateDealPricingPeriod = (pricingPeriod: DealPricingPeriod, type: MultiplePricingBlockType) => {
        if (!deal) return
        let existingPricingPeriods = type === 'Final' ? deal.finalPricing.pricingPeriods : deal.provisionalPricing.pricingPeriods
        let existingPriceIndexes = type === 'Final' ? deal.finalPricing.priceIndexes : deal.provisionalPricing.priceIndexes
        let newPricingPeriods: DealPricingPeriod[] = ((existingPricingPeriods.some(x => pricingPeriod.id == x.id))
            ? resetPricingPeriodsAfterPricingChange(existingPricingPeriods.map(x => (x.id === pricingPeriod.id) ? pricingPeriod : x), existingPriceIndexes, type, pricingPeriod.id)
            : existingPricingPeriods.concat(pricingPeriod))
        if (type === 'Final')
            setDeal({ ...deal, finalPricing: { ...deal.finalPricing, pricingPeriods: newPricingPeriods } })
        if (type === 'Provisional')
            setDeal({ ...deal, provisionalPricing: { ...deal.provisionalPricing, pricingPeriods: newPricingPeriods } })
    }

    let removeDealPriceIndex = (id: Guid, type: MultiplePricingBlockType) => {
        if (!deal) return
        let existingPriceIndexes = type === 'Final' ? deal.finalPricing.priceIndexes : deal.provisionalPricing.priceIndexes
        let existingPricingPeriods = type === 'Final' ? deal.finalPricing.pricingPeriods : deal.provisionalPricing.pricingPeriods
        let newDealPriceIndexes = existingPriceIndexes.filter(x => x.id !== id)
        let newPricingPeriods = resetPricingPeriodsAfterPricingChange(existingPricingPeriods, newDealPriceIndexes, type)

        if (type === 'Final')
            setDeal({ ...deal, finalPricing: { ...deal.finalPricing, priceIndexes: newDealPriceIndexes, pricingPeriods: newPricingPeriods } })
        if (type === 'Provisional')
            setDeal({ ...deal, provisionalPricing: { ...deal.provisionalPricing, priceIndexes: newDealPriceIndexes, pricingPeriods: newPricingPeriods } })
    }

    let resetPricingPeriodsAfterPricingChange =
        (pricingPeriods: DealPricingPeriod[],
            updatedPriceIndexes: DealPriceIndex[],
            type: MultiplePricingBlockType,
            targetId: Guid | null = null
        ): DealPricingPeriod[] => {
            let updatedPremiumUnit = getPricingPeriodPremiumUnit(type, updatedPriceIndexes)
            return pricingPeriods.map(x => (!!targetId && targetId !== x.id) ? x : ({ ...x, baseUnitPrice: null, premiumUnit: updatedPremiumUnit }))
        }

    let removeDealPricingPeriod = (id: Guid, type: MultiplePricingBlockType) => {
        if (!deal) return
        let existingPricingPeriods = type === 'Final' ? deal.finalPricing.pricingPeriods : deal.provisionalPricing.pricingPeriods
        let newPricingPeriods = existingPricingPeriods.filter(x => x.id !== id)

        if (type === 'Final')
            setDeal({ ...deal, finalPricing: { ...deal.finalPricing, pricingPeriods: newPricingPeriods } })
        if (type === 'Provisional')
            setDeal({ ...deal, provisionalPricing: { ...deal.provisionalPricing, pricingPeriods: newPricingPeriods } })
    }

    let saveOpenSectionsInLocalStorage = () => {
        setInLocalStorage('dealOpenSections', openSections)
    }

    let setDealStatus = (deal: Deal) => {
        if (deal.referenceNumber && deal.movements && deal.movements.length != 0)
            deal.kind = DealKind.Linked
        else if (deal.referenceNumber && (!deal.movements || deal.movements.length == 0))
            deal.kind = DealKind.Autonomous
        else if (!deal.referenceNumber && (!deal.movements || deal.movements.length == 0))
            deal.kind = !deal.dealType ? DealKind.Creating : DealKind.Duplicate
    }

    let initializeNewDeal = (deal: Deal, pricingType: DealPricingType, dealType?: string) => {
        deal.pricingType = pricingType
        deal.validFrom = ''
        deal.validTo = ''
        deal.country = user.currentCountry
        deal.isManual = true

        if (dealType === "BORROWLOAN") {
            initializeBorrowLoan(deal, dealType)
        }
    }

    let initializeBorrowLoan = (deal: Deal, dealType?: string) => {
        deal.dealType = dealType!
        deal.meanOfTransportation = "InTank"
        deal.site = null
        deal.termSpot = 'Spot'
    }

    let getDealInfos = async (dealId: Guid | null, pricingType?: DealPricingType, dealType?: string) => {
        if (!dealId) return null
        let vesselPromise = api.get<VesselInfo[]>(`vessel/fromDeal?dealId=${dealId}`)
        let deal = await dealApi.get(dealId)
        setDealStatus(deal)
        if (deal.kind == DealKind.Creating)
            initializeNewDeal(deal, pricingType!, dealType)
        let vessels = await vesselPromise
        initializeDealPriceIndexesAndPricingPeriods(deal)
        getForecastDailyPurchaseStatus(deal)
        return { deal, vessels }
    }

    let initializeDealPriceIndexesAndPricingPeriods = (deal: Deal) => {
        if (hasFormulaGenericPricingDefinition()) {
            deal.finalPricing.priceIndexes = initializeIfEmptyIndexes(deal.finalPricing.priceIndexes)
            deal.finalPricing.pricingPeriods = initializeIfEmptyPeriods(deal.finalPricing.pricingPeriods, deal.quantityPriceUnit)

            if (hasFeature('ProvisionalPricing')) {
                deal.provisionalPricing.priceIndexes = initializeIfEmptyIndexes(deal.provisionalPricing.priceIndexes)
                deal.provisionalPricing.pricingPeriods = initializeIfEmptyPeriods(deal.provisionalPricing.pricingPeriods, deal.quantityPriceUnit)
            }
        }
    }

    let initializeIfEmptyIndexes = (priceIndexes: DealPriceIndex[]) => {
        return (priceIndexes.length > 0)
            ? priceIndexes
            : [{ id: guid.createNew(), percentage: 1, priceIndexCode: '' }]
    }

    let initializeIfEmptyPeriods = (pricingPeriods: DealPricingPeriod[], quantityPriceUnit: string | null) => {
        return (pricingPeriods.length > 0)
            ? pricingPeriods
            : [{
                id: guid.createNew(),
                percentage: 1,
                start: null,
                end: null,
                premium: 0,
                baseUnitPrice: null,
                premiumUnit: quantityPriceUnit ?? "?",
                formulaType: null,
                startOverwritten: false,
                endOverwritten: false
            }]
    }

    let getUnitFromDealProduct = async (productId: string | null | undefined): Promise<string | undefined> => {
        if (!productId) return undefined
        return products.find(x => x.id === productId)?.unit
    }

    let getCurrencyFromDealCompany = async (companyCode: string | null | undefined): Promise<string | undefined> => {
        if (!companyCode) return undefined
        return companys.find(x => x.code === companyCode)?.companyCurrency
    }

    let getRefs = async (country: string) => {
        return {
            companys: await dealApi.getCompanys(),
            dutyStatuses: [...new Set<string>((await dealApi.getCompanys())
                .reduce((acc, cur) => acc.concat(cur.dutyStatuses), new Array<string>()))],
            counterpartys: await dealApi.searchCounterpartys(country, false),
            sites: await dealApi.getSites(),
            products: await dealApi.getProductCodes(),
            paymentTerms: (await dealApi.getPaymentTerms()).map(x => x.code),
            dealTypes: await dealApi.getDealTypes(),
            quantityUnitPrices: await dealApi.getQuantityCurrencyUnitPrices(),
            meanOftransportations: await dealApi.getMot(),
            sapCounterparty: await dealApi.getSapCounterpartys(),
            allCounterpartys: await dealApi.searchCounterpartys(country, true),
            pricingPeriodRules: await dealApi.getPricingPeriodRules()
        }
    }

    let getAllowedTypes = (dealTypes: string[]) => {
        return dealTypes.filter(x => hasScope2OnClaim(Claims.DealManager, x))
    }

    let loadDeal = async (dealId: Guid | null, pricingType?: DealPricingType, dealType?: string): Promise<Deal | null> => {
        let dealInfosPromise = getDealInfos(dealId, pricingType, dealType)
        let dealInfos = await dealInfosPromise
        if (!dealInfos) return null
        let { deal, vessels } = dealInfos
        let refs = shouldInit() ? await getRefs(deal.country) : null
        ReactDOM.unstable_batchedUpdates(() => {
            fieldStatus.clearStatuses()
            if (refs) {
                setCounterpartys(refs.counterpartys)
                setDutyStatuses(refs.dutyStatuses)
                setCompanys(refs.companys)
                setSites(refs.sites)
                setProducts(refs.products)
                setPaymentTerms(refs.paymentTerms)
                setDealTypes(getAllowedTypes(refs.dealTypes))
                setQuantityUnitPrices(refs.quantityUnitPrices)
                setMeanOfTransportations(refs.meanOftransportations)
                setSapCounterpartys(refs.sapCounterparty)
                setAllCounterpartys(refs.allCounterpartys)
                setPricingPeriodRules(refs.pricingPeriodRules)
            }
            setAssociatedVessels(vessels)
            setDeal(deal)
            let dealComapnys = refs?.companys ?? companys
            if (dealComapnys.length) {
                let company = dealComapnys.find(x => x.code === deal.company)
                setDutyStatuses(company?.dutyStatuses ?? [])
            }
            setDealId(dealId)
        })

        if (dealId) window.history.pushState({}, '', '?openDeal=' + dealId)

        return deal
    }

    let saveDeal = async (refreshAfterSave = true): Promise<void> => {
        if (!dealId || !deal) return
        if (deal.status === DealStatus.Closed && isBorrowLoan() && (movementsVolume !== 0 || actualMovementsVolume !== 0))
            snackbars.warning(t('deals.warning.unbalancedVolumes'))

        let waitCorrelationId: string | undefined
        try {
            waitCorrelationId = (await dealApi.post(deal)).correlationId
            setLastCorrelationId(waitCorrelationId)
            getForecastDailyPurchaseStatus(deal)
            if (forecastDailyPurchaseStatus === 'Contributes')
                snackbars.success(t('httpSuccess.dealAndPurchasesSaved'))
            else
                snackbars.success(t('httpSuccess.dealSaved'))
        } catch (err) {
            if (err.status !== 409) throw err
        }
        if (refreshAfterSave) void refreshDeal(waitCorrelationId)
    }

    let duplicateDeal = async (command: DuplicateDealCommand): Promise<void> => {
        try {
            setLastCorrelationId((await dealApi.duplicateDeal(command)).correlationId)
            snackbars.success(t('httpSuccess.dealDuplicated'))
        } catch (err) {
            if (err.status !== 409) throw err
        }
    }

    let assignMovements = async (movementIds: string[] | undefined): Promise<void> => {
        setLastCorrelationId(
            (await dealApi.assignMovements(deal?.id, movementIds)).correlationId)
        refreshDeal()
    }

    let unAssignMovement = async (movementId: string | undefined): Promise<void> => {
        setLastCorrelationId(
            (await dealApi.unassignMovement(deal?.id, movementId)).correlationId)
        refreshDeal()
    }

    let refreshDeal = async (waitCorrelationId = lastCorrelationId) => {
        let currentDeal = await dealApi.get(dealId!, waitCorrelationId)
        initializeDealPriceIndexesAndPricingPeriods(currentDeal)
        setDealStatus(currentDeal)
        setDeal(currentDeal)
    }

    let getForecastDailyPurchaseStatus = async (deal: Deal) => {
        let forecastDailyPurchaseStatus = await dealApi.getForecastDailyPurchaseStatus(deal!.id)
        setForecastDailyPurchaseStatus(forecastDailyPurchaseStatus.status)
    }

    let lockedByVessel = associatedVessels.length > 0 && hasFeature('VesselAutoCreateDeal')
    let lockedByClaim = hasClaim(Claims.DealManager) && !hasScope2OnClaim(Claims.DealManager, deal?.dealType ?? '')
    let locked = (isLockedFromVessel: boolean = true) => (isLockedFromVessel && lockedByVessel) || lockedByClaim
    let pricingFieldsLocked = (type: string) => (lockedByVessel && type !== 'Provisional') || lockedByClaim

    let shouldInit = () => counterpartys.length == 0 || companys.length == 0 || sites.length == 0 || products.length == 0 || priceIndexes.length == 0
    let isAssociatedToAMovement = () => !!deal && !!deal.movements?.length
    let isAutonomous = () => !!deal && deal.kind == DealKind.Autonomous
    let isCreating = () => !!deal && deal.kind == DealKind.Creating
    let isDuplicate = () => !!deal && deal.kind == DealKind.Duplicate
    let canAssignMovement = () => hasFeature('AssignMovementFromDeal')
    let isSaleOnTruckDeal = () => hasFeature('SupplyMktSalesTruckDeal')
        && deal?.meanOfTransportation === "Road"
        && deal?.movementType === DealMovementType.SALE
    let isPaperTransaction = () => !!deal && deal?.dealType === DealTypes.PAPER || deal?.dealType === DealTypes.EXPOADJ || deal?.dealType === DealTypes.EXPOINI

    let sapCounterpartyShipToCode = sapCounterpartys.find(x => x.counterpartyId == deal?.counterpartyId!)?.shipToCode
    let autoCreateOnPurchase = () => hasFeature('AutoCreateDealOnPurchase') && !hasFeature('SelectPricingType')
        && deal?.movementType === DealMovementType.PURCHASE && isAssociatedToAMovement()

    let existInSapShipToTable = () => shipTo.length > 0 && shipTo.find(x => x.code === sapCounterpartyShipToCode)?.exists
    let isInternal = (dealType?: string | null) => {
        dealType = dealType ?? deal?.dealType
        return dealType === "INTERNAL"
    }
    let isMotMandatory = () => !isPaperTransaction()

    let hasFormulaGenericPricingDefinition = (): boolean => {
        return deal?.pricingType == 'Formula' && (hasFeature('GenericPricing') || isInternal(deal?.dealType))
    }

    let hasFormulaGenericPricingResults = (): boolean => {
        return deal?.pricingType == 'Formula' && (hasFeature('GenericPricing') && !isInternal(deal?.dealType))
    }

    let hasQuantityPriceUnit = (): boolean => {
        return hasFeature('DealQuantityCurrencyUnitPrice') || isInternal(deal?.dealType) || isBorrowLoan()
    }

    let hasActiveStatus = () => deal?.status !== DealStatus.Cancelled && deal?.status !== DealStatus.Closed

    let hasMovementsOrMktSales = () => isInternal()
        || isAssociatedToAMovement()
        || deal?.generateMktSaleForecasts
        || forecastDailyPurchaseStatus === ForecastDailyPurchaseStatus.Contributes

    let cannotEditProductAndSite = () => hasMovementsOrMktSales()
        || !!deal?.contractReference
        || locked()

    let open = async (id: Guid, pricingType?: DealPricingType, dealType?: string) => {
        dialog.setIsOpen(true)
        await loadDeal(id, pricingType, dealType)
    }

    let close = () => {
        ReactDOM.unstable_batchedUpdates(() => {
            setCounterpartys([])
            setCompanys([])
            setSites([])
            setPriceIndexes([])
            setProducts([])
            dialog.setIsOpen(false)
            fieldStatus.clearStatuses()
            setDealId(null)
            setAssociatedVessels([])
            setDeal(null)
            setQuantityUnitPrices([])
            setSapCounterpartys([])
            setShipTo([])
        })
        window.history.pushState({}, document.title, window.location.pathname)
    }

    let highlightErrors = () => {
        const alertStatus = 'alert'
        fieldStatus.clearStatuses()
        if (!deal?.company)
            fieldStatus.setStatus('company', alertStatus)
        if (!!deal?.counterpartyId) {
            let counterpartyName = counterpartys.find(x => x.id === deal?.counterpartyId)?.name
            let companyName = companys.find(x => x.code === deal?.company)?.name
            if (counterpartyName === companyName) {
                fieldStatus.setStatus('counterpartyId', alertStatus)
            }
        }
        if (!deal?.dutyStatus)
            fieldStatus.setStatus('dutyStatus', alertStatus)
        if (!deal?.referenceNumber && !isInternal())
            fieldStatus.setStatus('referenceNumber', alertStatus)
        if (!deal?.movementType)
            fieldStatus.setStatus('movementType', alertStatus)
        if (!deal?.dealType)
            fieldStatus.setStatus('dealType', alertStatus)
        if (!deal?.site && !isInternal() && !isPaperTransaction() && !isBorrowLoan())
            fieldStatus.setStatus('site', alertStatus)
        if (!deal?.productId)
            fieldStatus.setStatus('productId', alertStatus)
        if (!deal?.validFrom)
            fieldStatus.setStatus('validFrom', alertStatus)
        if (!deal?.validTo)
            fieldStatus.setStatus('validTo', alertStatus)
        if (isMotMandatory() && !deal?.meanOfTransportation)
            fieldStatus.setStatus('meanOfTransportation', alertStatus)
        if (deal?.volume === null && deal?.quantity === null)
            fieldStatus.setStatus(['volume', 'quantity'], alertStatus)
        if (!deal?.quantityPriceUnit && hasFeature('DealQuantityCurrencyUnitPrice'))
            fieldStatus.setStatus('quantityPriceUnit', alertStatus)
        if (!deal?.quantityUnit && hasFeature('DealQuantityCurrencyUnitPrice'))
            fieldStatus.setStatus('quantityUnit', alertStatus)
    }

    let updateDealCompany = async (newCompanyCode: string) => {
        if (deal == null) return
        setDeal({ ...deal, company: newCompanyCode })
        let company = companys.find(x => x.code === newCompanyCode)
        if (company) setDutyStatuses(company.dutyStatuses)
    }

    let toggleSection = (section: CollapsableSection) => {
        let newOpenSection = openSections
        newOpenSection[section] = !openSections[section]
        setOpenSections({ ...newOpenSection })
        saveOpenSectionsInLocalStorage()
    }

    let createMovement = async (command: CreateMovementFromDeal) => {
        let correlationId: string | undefined
        try {
            correlationId = (await dealApi.createMovementFromDeal(command)).correlationId
            setLastCorrelationId(correlationId)
            void refreshDeal(correlationId)
            snackbars.success(t('httpSuccess.movementCreated'))
        } catch (err) {
            if (err.status !== 409) throw err
        }
        return correlationId
    }

    let forecastMktSales = async (command: DealForecastMktSales) => {
        try {
            await dealApi.applyMktSaleForecast(command)
            if (!!deal)
                await loadDeal(deal?.id ?? null, deal.pricingType)
        } catch (err) {
            if (err.status !== 409) throw err
        }
    }

    let forecastDailyPurchases = async () => {
        await saveDeal()
        try {
            setLastCorrelationId((await dealApi.createDailyDealMovements(deal?.id)).correlationId)
            snackbars.success(t('deals.label.dailyPurchasesForecasted'))

            refreshDeal()
            getForecastDailyPurchaseStatus(deal!)
        } catch (err) {
            if (err.status !== 409) throw err
        }
    }

    let createMovementQueryObject = () => {
        if (!deal) return
        let isIn = GetIsIn(GetMovementType(deal.movementType))
        let queryObject = {
            site: deal!.site,
            company: deal!.company,
            dutystatus: deal!.dutyStatus,
            productIds: deal!.productId,
            movementType: GetMovementType(deal!.movementType),
            start: deal!.validFrom,
            end: deal!.validTo,
            in: isIn
        }
        return queryObject
    }

    let getTranslatedMovementType = (dealMovementType?: string): string => {
        let tBase = 'deals.types.movement.'
        switch (dealMovementType) {
            case DealMovementType.SALE.toString(): return t(tBase + 'sale')
            case DealMovementType.PURCHASE.toString(): return t(tBase + 'purchase')
            case DealMovementType.STATUSCHANGE.toString(): return t(tBase + 'statusChange')
            case DealMovementType.TRANSFER.toString(): return t(tBase + 'transfer')
            case DealMovementType.REBRANDING.toString(): return t(tBase + 'rebranding')
            case DealMovementType.BORROW.toString(): return t(tBase + 'borrow')
            case DealMovementType.LOAN.toString(): return t(tBase + 'loan')
            default: return ''
        }
    }

    dialogRef = useRef<OpenCloseDialogRef>(null)
    useImperativeHandle(dialogRef, () => ({ open, close }))

    let movementsVolume = isInternal()
        ? deal?.movementQuantity
        : deal?.movements?.reduce((acc, cur) => acc + (cur.volume ?? 0), 0)

    let actualMovementsVolume = isBorrowLoan()
        ? deal?.movements?.filter(x => x.movementStatus === MovementStatus.Actual)
            .reduce((acc, cur) => acc + (cur.volume ?? 0), 0)
        : 0

    let getGlobalPricingPeriod = (type: MultiplePricingBlockType): Period => {
        let period: Period = { from: null, to: null }
        let existingPricingPeriods = type === 'Final' ? deal?.finalPricing.pricingPeriods : deal?.provisionalPricing.pricingPeriods
        if (!deal || existingPricingPeriods?.length === 0) return period
        existingPricingPeriods?.forEach(x => {
            if (!!x.start && (!period.from || moment(period.from) >= moment(x.start)))
                period.from = x.start
            if (!!x.end && (!period.to || moment(period.to) <= moment(x.end)))
                period.to = x.end
        })
        return period
    }

    let getFirstPricingPeriod = (type: MultiplePricingBlockType): Period => {
        let period: Period = { from: null, to: null }
        let existingPricingPeriods = type === 'Final' ? deal?.finalPricing.pricingPeriods : deal?.provisionalPricing.pricingPeriods
        if (!deal || existingPricingPeriods?.length === 0) return period
        let firstPeriod = existingPricingPeriods?.first()
        if (firstPeriod) {
            period.from = firstPeriod.start
            period.to = firstPeriod.end
        }
        return period
    }

    let getPricingPeriodPremiumUnit = (type: MultiplePricingBlockType, updatedPriceIndexes: DealPriceIndex[] | undefined = undefined): string => {
        if (!deal) return "?"

        let oldPriceIndexes = type === 'Final' ? deal.finalPricing.priceIndexes : deal.provisionalPricing.priceIndexes
        let dealPriceIndexes = updatedPriceIndexes ?? oldPriceIndexes
        let firstPriceIndex = priceIndexes.find(x => x.medecoCode === dealPriceIndexes[0]?.priceIndexCode)
        let premiumUnit = !!firstPriceIndex ? [firstPriceIndex?.currency, firstPriceIndex?.unitOfMeasurement].join('/') : deal?.quantityPriceUnit
        return premiumUnit ?? "?"
    }

    let changeFieldValue = (code: string, newValue: number | null) => {
        if (!deal) return
        let index = deal.fields.findIndex(x => x.code == code)
        deal.fields[index].price = newValue
        setDeal({ ...deal })
    }

    let resolveDealFieldUnit = (fieldUnit: string, pricingType: DealPricingType, isGenericPricingActive: boolean, group: DealFieldGroup): string => {
        if (!deal) return fieldUnit
        let priceIndexUnit = pricingType === 'Formula' && isGenericPricingActive ? getPriceIndexUnit(group) ?? deal.quantityPriceUnit : deal.quotationPeriod.quotation.unit
        let priceIndexCurrency = priceIndexUnit?.split('/')[0]
        let premiumUnit = getPremiumUnit(group)
        return fieldUnit
            .replace('ProvisionalIndexPriceUnit', priceIndexUnit as string ?? '?')
            .replace('ProvisionalQuotationCurrency', priceIndexCurrency ?? '?')
            .replace('DealCurrency', deal.currency === 'Invalid' ? '?' : deal.currency ?? '?')
            .replace('IndexPriceUnit', priceIndexUnit as string ?? '?')
            .replace('QuotationCurrency', priceIndexCurrency ?? '?')
            .replace('QuantityPriceUnit', deal.quantityPriceUnit ?? '?')
            .replace('ProvisionalPremiumUnit', premiumUnit ?? '?')
            .replace('PremiumUnit', premiumUnit ?? '?')
            .replace('ProductUnit', deal.quantityUnit ?? '?')
    }

    //note : this behaviour should be synchronized with the method PricingEngine>DynamizeUnit.Map
    let resolveExchangeRateUnit = (exchangeRateDynamicUnit: string,
        priceIndexCurrency: string | undefined,
        type: MultiplePricingBlockType = 'Final') => {
        if (!deal) return exchangeRateDynamicUnit
        let currencyDynamicUnit: DealFieldUnit = type === 'Final'
            ? 'QuotationCurrency'
            : 'ProvisionalQuotationCurrency'
        let dealCurrencyUnit: DealFieldUnit = 'DealCurrency'
        let companyCurrencyUnit: DealFieldUnit = 'CompanyCurrency'
        return `${exchangeRateDynamicUnit
            .replace(dealCurrencyUnit, deal.currency === 'Invalid' ? '?' : deal.currency ?? '?')
            .replace(companyCurrencyUnit, currency ?? '?')
            .replace(currencyDynamicUnit, priceIndexCurrency ?? '?')}`
    }

    let getPriceIndexUnit = (group: DealFieldGroup): string | undefined => {
        let priceIndexUnit
        if (!deal) return
        if (group === 'default') {
            let finalDealPriceIndexCode = deal?.finalPricing.priceIndexes.first()?.priceIndexCode
            priceIndexUnit = priceIndexes.find(x => x.medecoCode === finalDealPriceIndexCode)
            return !!priceIndexUnit ? `${priceIndexUnit.currency}/${priceIndexUnit.unitOfMeasurement}` : undefined
        }

        if (group === 'provisional') {
            let provisionalDealPriceIndexCode = deal?.provisionalPricing.priceIndexes.first()?.priceIndexCode
            priceIndexUnit = priceIndexes.find(x => x.medecoCode === provisionalDealPriceIndexCode)
            return !!priceIndexUnit ? `${priceIndexUnit.currency}/${priceIndexUnit.unitOfMeasurement}` : undefined
        }
    }

    let getPremiumUnit = (group: DealFieldGroup): string | undefined => {
        if (!deal) return
        if (group === 'default') {
            return deal?.finalPricing.pricingPeriods.first()?.premiumUnit
        }

        if (group === 'provisional') {
            return deal?.provisionalPricing.pricingPeriods.first()?.premiumUnit
        }
    }

    let onDealPeriodChange = (period: DealDuplicatePeriod) => {
        setDuplicatePeriod({ start: period.start!, end: period.end! })
    }

    let isDuplicateByPeriodAndNotInternal = hasFeature("DuplicateDealNextMonth") && !isInternal()

    let addOneMonth = (date) => {
        let momentDate = moment(date)
        let oneMonthLater = momentDate.date() === momentDate.daysInMonth()
            ? momentDate.add(1, 'month').endOf('month')
            : momentDate.add(1, 'month')
        return oneMonthLater.format('YYYY/MM/DD')
    }

    async function saveIfNoFieldErrors(refreshAfterSave = true): Promise<boolean> {
        highlightErrors()
        if (fieldStatus.getFieldsWithStatus('alert').length === 0) {
            await saveDeal(refreshAfterSave)
            return true
        }
        else {
            snackbars.warning(t('warnings.mandatoryFieldsBeforeSaving'))
            return false
        }
    }

    let getQuotationCalendar = async (medecoCode: string) => {
        let quotationCalendar = await api.get<{ id: string, office: string, date: string }[]>(`pricing/priceIndex/${medecoCode}/quotationCalendar`)
        return quotationCalendar.map(x => x.date)
    }

    function isBorrowLoan() {
        return deal?.dealType === "BORROWLOAN"
    }

    return {
        deal, setDeal, counterpartys, dutyStatuses, dealTypes, updateDealCompany, loadDeal, saveDeal, duplicateDeal,
        assignMovements, unAssignMovement, associatedVessels, locked, companys, sites, products, priceIndexes, paymentTerms,
        dealId, setDealId, isAssociatedToAMovement, unit, assigneeChoices, fieldStatus, costStatus, highlightErrors,
        isCreating, isAutonomous, isDuplicate, openSections, toggleSection, movementsVolume, createMovement,
        quantityUnitPrices, setAssignableMovements, assignableMovements, canAssignMovement, setSelectedMovements, selectedMovements,
        assignConfirmDialogOpen, setAssignConfirmDialogOpen, unAssignConfirmDialogOpen, setUnAssignConfirmDialogOpen,
        dealMovement, setDealMovement, currency, meanOfTransportations, forecastMktSales, isSaleOnTruckDeal,
        removeDealPriceIndex, existInSapShipToTable, getTranslatedMovementType, addOrUpdateDealPriceIndex,
        addOrUpdateDealPricingPeriod, removeDealPricingPeriod, getGlobalPricingPeriod, getPricingPeriodPremiumUnit, filters, setFilters,
        forecastDailyPurchases, isInternal, allCounterpartys, changeFieldValue, isPaperTransaction,
        hasActiveStatus, resolveDealFieldUnit, resolveExchangeRateUnit, saveIfNoFieldErrors, forecastDailyPurchaseStatus,
        cannotEditProductAndSite, onDealPeriodChange, duplicatePeriod, isDuplicateByPeriodAndNotInternal, isPeriodMode,
        setIsPeriodMode, addOneMonth, setDuplicatePeriod, hasMovementsOrMktSales, dealStatusDialog,
        setDealStatusDialog, hasFormulaGenericPricingDefinition, hasFormulaGenericPricingResults,
        hasQuantityPriceUnit, getFirstPricingPeriod, pricingFieldsLocked, pricingPeriodRules, getQuotationCalendar, isBorrowLoan, actualMovementsVolume,
        defaultQuantityPriceUnit, autoCreateOnPurchase
    }
}

let defaultFilters = {
    movementTypes: [],
    dateOn: moment.utc().format(isoDateFormat),
    startDate: moment.utc().format(isoDateFormat),
    endDate: moment.utc().format(isoDateFormat),
    displayPricingPeriodList: false,
    isDayPeriodMode: false,
    productIds: [],
    sites: [],
    pricingTypes: [],
}

let getLocalStorageFilters = (): DealFilter => {
    let localFilters = localStorage.getItem('dealFilters')

    if (localFilters) {
        let filterObj = JSON.parse(localFilters)
        let filtersLength = Object.keys(filterObj).length

        if (filtersLength === 0) return defaultFilters

        let parseArray = x => Array.isArray(x) ? x : null

        return {
            dateOn: filterObj.dateOn,
            startDate: filterObj.startDate,
            endDate: filterObj.endDate,
            displayPricingPeriodList: filterObj.displayPricingPeriodList,
            companys: parseArray(filterObj.companys),
            counterpartyIds: parseArray(filterObj.counterpartyIds),
            dealTypes: parseArray(filterObj.dealTypes),
            dutyStatuss: parseArray(filterObj.dutyStatuss),
            productIds: parseArray(filterObj.productIds),
            isDayPeriodMode: filterObj.isDayPeriodMode,
            movementTypes: parseArray(filterObj.movementTypes) as string[],
            sites: parseArray(filterObj.sites),
            pricingTypes: parseArray(filterObj.pricingTypes),
            formulaTypes: parseArray(filterObj.formulaTypes),
        }
    }

    return defaultFilters
}

type OpenCloseDialogRef = { open: (id: Guid, pricingType?: DealPricingType, dealType?: string) => void, close: () => void }
let dialogRef: RefObject<OpenCloseDialogRef> | null = null
export let dealDialog = {
    open: (id, pricingType?, dealType?) => dialogRef?.current?.open(id, pricingType, dealType),
    close: () => dialogRef?.current?.close(),
}

export let DealDialogContainer = createContainer(useDealDialog)
export let DealContainer = createContainer(useDeal)

export type DealContainerStore = ReturnType<typeof useDeal>